// Statuses of the new interface (docs/design-system.md §0.8): one shape and one
// hue per state, the same in lists, timelines and details. Plain TypeScript so
// the mapping is tested without a DOM.

/** A hue from the status palette; `accent` is the one accent, used only for selection. */
export type Tone = "gray" | "amber" | "teal" | "violet" | "orange" | "green" | "red" | "slate" | "accent";

/**
 * How a status icon is drawn: a dashed ring (waiting for review), a ring (not
 * started), a ring filling up (in progress), a filled disc with a hole (a
 * decision is due), or a filled disc with a mark (a result or an end).
 */
export type Shape = "dashed" | "ring" | "pie" | "due" | "check" | "dash" | "down" | "question" | "cross" | "pause";

export const STATUS_KINDS = [
  "proposed", "approved", "not_started", "running", "yours", "delivered", "live", "observing", "measuring",
  "due", "done", "positive", "accepted", "none", "negative", "inconclusive",
  "rejected", "expired", "failed", "stopped", "paused",
] as const;
export type StatusKind = (typeof STATUS_KINDS)[number];

interface Meta { tone: Tone; shape: Shape; progress?: number }

const META: Record<StatusKind, Meta> = {
  proposed: { tone: "gray", shape: "dashed" },
  approved: { tone: "gray", shape: "ring" },
  not_started: { tone: "gray", shape: "ring" },
  running: { tone: "amber", shape: "pie", progress: 0.5 },
  yours: { tone: "teal", shape: "pie", progress: 0.75 },
  delivered: { tone: "teal", shape: "pie", progress: 1 },
  live: { tone: "teal", shape: "check" },
  observing: { tone: "violet", shape: "pie", progress: 0.5 },
  measuring: { tone: "violet", shape: "pie", progress: 0.5 },
  due: { tone: "orange", shape: "due" },
  done: { tone: "green", shape: "check" },
  positive: { tone: "green", shape: "check" },
  accepted: { tone: "green", shape: "check" },
  none: { tone: "gray", shape: "dash" },
  negative: { tone: "red", shape: "down" },
  inconclusive: { tone: "slate", shape: "question" },
  rejected: { tone: "gray", shape: "cross" },
  expired: { tone: "gray", shape: "cross" },
  failed: { tone: "red", shape: "cross" },
  stopped: { tone: "gray", shape: "cross" },
  paused: { tone: "gray", shape: "pause" },
};

export const isStatusKind = (value: string): value is StatusKind => Object.hasOwn(META, value);

/** The tone and shape of a status; `progress` (0–1) fills a ring, otherwise the state's default. */
export function statusMeta(kind: StatusKind, progress?: number | null): { tone: Tone; shape: Shape; progress: number | null } {
  const meta = META[kind];
  if (meta.shape !== "pie") return { tone: meta.tone, shape: meta.shape, progress: null };
  const value = progress ?? meta.progress ?? 0;
  return { tone: meta.tone, shape: "pie", progress: Math.min(1, Math.max(0, value)) };
}

/** The SVG path of a ring's filled share, centred at (8, 8) with radius 3.6; null when empty, "full" when complete. */
export function piePath(fraction: number): string | "full" | null {
  const f = Math.min(1, Math.max(0, fraction));
  if (f <= 0) return null;
  if (f >= 1) return "full";
  const angle = f * 2 * Math.PI - Math.PI / 2;
  const x = 8 + 3.6 * Math.cos(angle);
  const y = 8 + 3.6 * Math.sin(angle);
  const large = f > 0.5 ? 1 : 0;
  return `M8 8V4.4A3.6 3.6 0 ${large} 1 ${x.toFixed(3)} ${y.toFixed(3)}Z`;
}
